import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Box from "@mui/material/Box";
import { ProjectLineItem } from "@interfaces/project";
import SaveIcon from "@mui/icons-material/Save";
import * as yup from "yup";
import { useFormik } from "formik";
import { SocialProcurementTableRow } from "../SocialProcurementTableRow/SocialProcurementTableRow";
import { ChangeEvent, FocusEvent, useEffect, useState } from "react";
import {
  SocialCommitmentLineItem,
  socialCommitmentTypeToString,
  TargetUnit,
} from "@interfaces/commitment";
import {
  StyledButton,
  StyledLoadingButton,
} from "@stories/atoms/StyledButton/StyledButton";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TargetModal from "@stories/organisms/TargetModal/TargetModal";
import { SubTitle } from "@stories/atoms/SubTitle/SubTitle";
import { ToastOptions, toast } from "react-toastify";

interface SocialRequirementFormProps {
  loading: boolean;
  project: ProjectLineItem;
  commitmentLineItems: SocialCommitmentLineItem[];
  onSubmit: (commitments: SocialCommitmentLineItem[]) => void;
  handleBack: () => void;
}

interface FormikField {
  name: string;
  title: string;
  metric: TargetUnit;
  label: string;
  outcomeMultiplier: number;
  initialValue: number;
  type: any;
}

export const SocialRequirementForm = ({
  loading,
  project,
  commitmentLineItems,
  onSubmit,
  handleBack,
}: SocialRequirementFormProps) => {
  const [commitments, setCommitments] =
    useState<SocialCommitmentLineItem[]>(commitmentLineItems);
  const [targetUnit, setTargetUnit] = useState<TargetUnit | undefined>(
    undefined
  );
  const [fields, setFields] = useState<FormikField[]>([]);
  const [initialValues, setInitialValues] = useState<{ [k: string]: number }>(
    {}
  );
  const [validationSchema, setValidationSchema] = useState<
    yup.ObjectSchema<
      {
        [x: string]: any;
      },
      yup.AnyObject,
      {
        [x: string]: any;
      },
      ""
    >
  >(yup.object());

  useEffect(() => {
    const fieldsFromCommitments: FormikField[] = commitments.map((c) => {
      return {
        name: c.id,
        title:
          c?.targetDescription || socialCommitmentTypeToString(c.targetName),
        outcomeMultiplier:
          c.targetUnit === TargetUnit.Spend
            ? project.requiredSpend
            : project.requiredHours,
        metric: c.targetUnit,
        label: socialCommitmentTypeToString(c.targetName),
        initialValue: c.targetValue,
        type: yup.number().required().min(0),
      } as FormikField;
    }) as FormikField[];

    const initialValues = Object.fromEntries(
      fieldsFromCommitments.map((field) => [field.name, field.initialValue])
    );

    const SchemaObject = Object.fromEntries(
      fieldsFromCommitments.map((field) => [field.name, field.type])
    );
    const newValidationSchema = yup.object().shape(SchemaObject);

    setFields(fieldsFromCommitments);
    setInitialValues(initialValues);
    setValidationSchema(newValidationSchema);
  }, [commitments]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (commitments.length > 0) {
        const updatedCommitments = commitments.map((c) => {
          return {
            id: c.id,
            targetName: c.targetName,
            targetUnit: c.targetUnit,
            targetValue: values[c.id],
            targetDescription: c.targetDescription,
            created: new Date(),
          };
        }) as SocialCommitmentLineItem[];
        onSubmit(updatedCommitments);
      } else {
        toast("Must have one target!", {
          type: "error",
        } as ToastOptions);
      }
    },
  });

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const createSocialCommitment = (
    socialCommitment: SocialCommitmentLineItem
  ) => {
    setCommitments([...commitments, socialCommitment]);
  };
  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <StyledCard>
          <Grid container spacing={2} sx={{ padding: "5%" }}>
            <Grid item xs={12} md={8}>
              <SubTitle title={`${project.projectName} Target Spend`} />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                alignContent: "right",
                justifyContent: "right",
                margin: "auto",
              }}
              justifyContent={"right"}
            >
              <StyledButton
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={() => {
                  setTargetUnit(TargetUnit.Spend);
                  setOpen(true);
                }}
              >
                Add Spend Target
              </StyledButton>
            </Grid>
            <Grid item xs={12} md={12}>
              <Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Commitment</TableCell>
                      <TableCell align="left">Target %</TableCell>
                      <TableCell align="center">Target Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields
                      .filter((f) => f.metric === TargetUnit.Spend)
                      .map(
                        ({ title, name, metric, outcomeMultiplier }, index) => {
                          return (
                            <SocialProcurementTableRow
                              key={`social-procurement-table-row-${index}`}
                              loading={loading || formik.isSubmitting}
                              title={title}
                              metric={metric}
                              valueId={name}
                              valueName={name}
                              value={formik.values[name]}
                              valueTouched={formik.touched[name]}
                              valueError={formik.errors[name]}
                              outcomeMultiplier={outcomeMultiplier}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                if (e.target.value) {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.value
                                  );
                                }
                              }}
                              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                                if (e.target.value) {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.value
                                  );
                                }
                              }}
                              onDelete={() => {
                                setCommitments(
                                  commitments.filter((f) => f.id !== name)
                                );
                              }}
                            />
                          );
                        }
                      )}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        </StyledCard>
        <StyledCard>
          <Grid container spacing={2} sx={{ padding: "5%" }}>
            <Grid item xs={12} md={8}>
              <SubTitle title={`${project.projectName} Target Hours`} />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                alignContent: "right",
                justifyContent: "right",
                margin: "auto",
              }}
              justifyContent={"right"}
            >
              <StyledButton
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={() => {
                  setTargetUnit(TargetUnit.Hour);
                  setOpen(true);
                }}
              >
                Add Hours Target
              </StyledButton>
            </Grid>
            <Grid item xs={12} md={12}>
              <Box>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">Commitment</TableCell>
                      <TableCell align="left">Target %</TableCell>
                      <TableCell align="center">Target Outcome</TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields
                      .filter((f) => f.metric === TargetUnit.Hour)
                      .map(
                        ({ title, name, metric, outcomeMultiplier }, index) => {
                          return (
                            <SocialProcurementTableRow
                              key={`social-procurement-table-row-${index}`}
                              loading={loading || formik.isSubmitting}
                              title={title}
                              metric={metric}
                              valueId={name}
                              valueName={name}
                              value={formik.values[name]}
                              valueTouched={formik.touched[name]}
                              valueError={formik.errors[name]}
                              outcomeMultiplier={outcomeMultiplier}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                if (e.target.value) {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.value
                                  );
                                }
                              }}
                              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                                if (e.target.value) {
                                  formik.setFieldValue(
                                    e.target.name,
                                    e.target.value
                                  );
                                }
                              }}
                              onDelete={() => {
                                setCommitments(
                                  commitments.filter((f) => f.id !== name)
                                );
                              }}
                            />
                          );
                        }
                      )}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        </StyledCard>
        <StyledCard>
          <Grid container spacing={2} sx={{ padding: "12px" }}>
            <Grid
              item
              xs={6}
              sm={6}
              sx={{
                display: "flex !important",
                justifyContent: "left !important;",
              }}
            >
              <StyledButton
                onClick={handleBack}
                disabled={loading || formik.isSubmitting}
              >
                Back
              </StyledButton>
            </Grid>
            <Grid
              item
              xs={6}
              sm={6}
              sx={{
                display: "flex !important",
                justifyContent: "right !important;",
              }}
            >
              <StyledLoadingButton
                color="primary"
                type="submit"
                loading={loading || formik.isSubmitting}
                disabled={loading || formik.isSubmitting}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="contained"
              >
                <span>Save</span>
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </StyledCard>
      </form>
      {targetUnit ? (
        <TargetModal
          open={open}
          loading={loading}
          targetType={targetUnit}
          existingSocialCommitments={commitments}
          createSocialCommitment={createSocialCommitment}
          handleClose={handleClose}
        />
      ) : null}
    </Box>
  );
};
